import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { useSubscription } from '../context/SubscriptionContext';
import { ArrowLeft, Trophy, Clock, Lightbulb, Volume2, VolumeX, Trash2, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import UpgradeModal from '../components/UpgradeModal';
import './AnagramGame.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOUNDS = {
  correct: 'https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3',
  wrong: 'https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3',
  click: 'https://assets.mixkit.co/active_storage/sfx/2000/2000-preview.mp3',
  victory: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3'
};

function AnagramGame() {
  const { user } = React.useContext(AuthContext);
  const { trackPlay, playedGamesCount, maxPlayedGames, canPlayMore, isPremium } = useSubscription();
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [availableLetters, setAvailableLetters] = useState([]);
  const [placedLetters, setPlacedLetters] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showEmoji, setShowEmoji] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  useEffect(() => { loadGames(); }, []);
  
  useEffect(() => {
    if (gameStarted && timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft(prev => prev <= 1 ? 0 : prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [gameStarted, timeLeft]);
  
  const loadGames = async () => {
    try {
      const response = await axios.get(`${API}/anagram`, { withCredentials: true });
      setGames(response.data);
    } catch (error) {
      console.error('Failed to load games:', error);
      toast.error('Failed to load games');
    }
  };
  
  const deleteGame = async (gameId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    try {
      await axios.delete(`${API}/anagram/${gameId}`, { withCredentials: true });
      toast.success('Game deleted!');
      loadGames();
    } catch (error) {
      console.error('Failed to delete game:', error);
      toast.error('Failed to delete game');
    }
  };
  
  const playSound = (soundName) => {
    if (!soundEnabled) return;
    try {
      const audio = new Audio(SOUNDS[soundName]);
      audio.volume = 0.3;
      audio.play().catch(e => console.log('Sound play failed:', e));
    } catch (error) {}
  };
  
  const splitWord = (word, type) => {
    if (type === 'syllables') {
      const syllables = word.match(/[^aeiou]*[aeiou]+/gi) || [word];
      return syllables;
    }
    return word.split('');
  };
  
  const shuffleArray = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };
  
  const startGame = async (game) => {
    // Check if user can play before tracking
    if (!isPremium && !canPlayMore) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Track play when user starts anagram game
    const result = await trackPlay(game.id, 'anagram');
    if (!result.success && result.limitReached) {
      setShowUpgradeModal(true);
      return;
    }
    
    setSelectedGame(game);
    setGameStarted(true);
    setCurrentWordIndex(0);
    setScore(0);
    setGameComplete(false);
    setShowConfetti(false);
    setShowHint(false);
    loadWord(game.words[0]);
    if (game.timer_setting === '30s') setTimeLeft(30);
    else if (game.timer_setting === '1min') setTimeLeft(60);
    else setTimeLeft(null);
  };
  
  const isArabic = (text) => {
    // Check if text contains Arabic characters (Unicode range)
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text);
  };

  const loadWord = (wordObj) => {
    const parts = splitWord(wordObj.word, wordObj.split_type);
    const shuffled = shuffleArray(parts);
    setAvailableLetters(shuffled);
    setPlacedLetters([]);
    setShowHint(false);
  };
  
  const handleLetterClick = (letter, fromAvailable) => {
    playSound('click');
    if (fromAvailable) {
      setAvailableLetters(availableLetters.filter((l, i) => i !== availableLetters.indexOf(letter)));
      setPlacedLetters([...placedLetters, letter]);
    } else {
      setPlacedLetters(placedLetters.filter((l, i) => i !== placedLetters.indexOf(letter)));
      setAvailableLetters([...availableLetters, letter]);
    }
  };
  
  const checkAnswer = () => {
    const currentWord = selectedGame.words[currentWordIndex];
    const userWord = placedLetters.join('').toLowerCase().trim();
    const correctWord = currentWord.word.toLowerCase().trim();
    
    if (userWord === correctWord) {
      playSound('correct');
      setScore(score + 10);
      setShowEmoji('correct');
      setTimeout(() => setShowEmoji(null), 1500);
      toast.success('Correct! 🎉');
      
      if (currentWordIndex < selectedGame.words.length - 1) {
        setTimeout(() => {
          setCurrentWordIndex(currentWordIndex + 1);
          loadWord(selectedGame.words[currentWordIndex + 1]);
        }, 1500);
      } else {
        setTimeout(() => {
          setGameComplete(true);
          setShowConfetti(true);
          playSound('victory');
          setTimeout(() => setShowConfetti(false), 5000);
        }, 1500);
      }
    } else {
      playSound('wrong');
      setShowEmoji('wrong');
      setTimeout(() => setShowEmoji(null), 1500);
      toast.error('Wrong! Try again!');
    }
  };
  
  const resetGame = () => {
    setGameStarted(false);
    setSelectedGame(null);
    setCurrentWordIndex(0);
    setAvailableLetters([]);
    setPlacedLetters([]);
    setScore(0);
    setTimeLeft(null);
    setShowHint(false);
    setGameComplete(false);
    setShowConfetti(false);
  };
  
  if (!gameStarted) {
    return (
      <div className="anagram-game-page">
        <div className="games-container">
          <div className="page-header-row">
            <h1>🔤 Anagram Games</h1>
            <Link to="/anagram-builder" className="create-new-btn">
              <Plus size={20} />
              Create New Anagram
            </Link>
          </div>
          <div className="games-grid">
            {games.length === 0 ? (
              <div className="empty-state">
                <p className="no-games">No anagram games yet. Create your first!</p>
                <Link to="/anagram-builder" className="btn btn-primary">
                  <Plus size={18} />
                  Create Anagram
                </Link>
              </div>
            ) : (
              games.map((game) => (
                <motion.div key={game.id} className="game-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ scale: 1.02 }}>
                  <div className="game-info">
                    <h3>{game.title}</h3>
                    <div className="game-details">
                      <span className="detail-badge">{game.words.length} Words</span>
                      {game.timer_setting !== 'off' && <span className="detail-badge timer-badge"><Clock size={14} />{game.timer_setting}</span>}
                    </div>
                    <div className="card-actions">
                      <Button onClick={() => startGame(game)} className="play-btn" disabled={!isPremium && !canPlayMore}>Play Now</Button>
                      {game.created_by === user?.id && (
                        <Button onClick={(e) => deleteGame(game.id, e)} variant="destructive" className="delete-btn">
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
        
        {/* Upgrade Modal for play limit */}
        <UpgradeModal 
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          limitType="play"
          currentCount={playedGamesCount}
          maxCount={maxPlayedGames || 3}
        />
      </div>
    );
  }
  
  const currentWord = selectedGame.words[currentWordIndex];
  
  return (
    <div className="anagram-game-page">
      {showConfetti && <Confetti />}
      <div className="game-container">
        <div className="game-header">
          <Button onClick={resetGame} className="back-btn" variant="ghost"><ArrowLeft size={20} />Back</Button>
          <div className="game-stats">
            <div className="stat"><Trophy size={20} /><span>{score} pts</span></div>
            {timeLeft !== null && <div className={`stat timer ${timeLeft <= 10 ? 'warning' : ''}`}><Clock size={20} /><span>{timeLeft}s</span></div>}
            <Button onClick={() => setSoundEnabled(!soundEnabled)} className="sound-toggle" variant="ghost">
              {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
            </Button>
          </div>
        </div>
        
        <h2 className="game-title">{selectedGame.title}</h2>
        <div className="word-progress">Word {currentWordIndex + 1} of {selectedGame.words.length}</div>
        
        {!gameComplete ? (
          <div className="anagram-play-area">
            {currentWord.image_url && (
              <div className="word-image-container">
                <img src={currentWord.image_url} alt="Word hint" className="word-image" />
              </div>
            )}
            
            {currentWord.hint && (
              <div className="hint-section">
                <Button onClick={() => setShowHint(!showHint)} className="hint-btn"><Lightbulb size={16} />{showHint ? 'Hide' : 'Show'} Hint</Button>
                {showHint && <p className="hint-text">💡 {currentWord.hint}</p>}
              </div>
            )}
            
            <div className="answer-area">
              <h3>Your Answer 👇</h3>
              <div 
                className="placed-letters-zone" 
                dir={isArabic(currentWord.word) ? 'rtl' : 'ltr'}
                style={{ direction: isArabic(currentWord.word) ? 'rtl' : 'ltr' }}
              >
                {placedLetters.length === 0 ? (
                  <div className="empty-placeholder">Click letters below to build the word</div>
                ) : (
                  placedLetters.map((letter, index) => (
                    <motion.div
                      key={`placed-${index}`}
                      className="letter-tile placed"
                      onClick={() => handleLetterClick(letter, false)}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {letter}
                    </motion.div>
                  ))
                )}
              </div>
            </div>
            
            <div className="letters-area">
              <h3>Available Letters 📝</h3>
              <div 
                className="available-letters-zone" 
                dir={isArabic(currentWord.word) ? 'rtl' : 'ltr'}
                style={{ direction: isArabic(currentWord.word) ? 'rtl' : 'ltr' }}
              >
                {availableLetters.map((letter, index) => (
                  <motion.div
                    key={`available-${index}`}
                    className="letter-tile available"
                    onClick={() => handleLetterClick(letter, true)}
                    whileHover={{ scale: 1.1, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {letter}
                  </motion.div>
                ))}
              </div>
            </div>
            
            <Button onClick={checkAnswer} disabled={placedLetters.length === 0} className="check-btn">Check Answer ✓</Button>
            
            <AnimatePresence>
              {showEmoji && (
                <motion.div className="emoji-feedback" initial={{ scale: 0, y: 0 }} animate={{ scale: 2, y: -50 }} exit={{ opacity: 0 }}>
                  {showEmoji === 'correct' ? '⭐🎉✨' : '❌😢'}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div className="game-complete">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }} className="complete-content">
              <Trophy size={64} color="#FFD700" />
              <h2>🎉 All Words Complete!</h2>
              <p className="final-score">Final Score: {score} points</p>
              <Button onClick={resetGame} className="play-again-btn">Play Another Game</Button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnagramGame;